import { useState, useEffect } from "react";
import { BookOpen, Search, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const bibleVersions = [
  { value: "KJV", label: "King James Version (KJV)" },
  { value: "NKJV", label: "New King James Version (NKJV)" },
  { value: "NIV", label: "New International Version (NIV)" },
  { value: "ESV", label: "English Standard Version (ESV)" },
  { value: "NASB", label: "New American Standard Bible (NASB)" },
  { value: "NLT", label: "New Living Translation (NLT)" },
];

const popularVerses = [
  { ref: "John 3:16", label: "John 3:16" },
  { ref: "Psalm 23", label: "Psalm 23" },
  { ref: "Romans 8:28", label: "Romans 8:28" },
  { ref: "Philippians 4:13", label: "Philippians 4:13" },
  { ref: "Jeremiah 29:11", label: "Jeremiah 29:11" },
  { ref: "Proverbs 3:5-6", label: "Proverbs 3:5-6" },
];

const BibleSearch = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [version, setVersion] = useState("KJV");

  // Re-initialize BibleGateway reference tagger after render
  useEffect(() => {
    if (window.BGLinks) {
      window.BGLinks.version = "NIV";
      window.BGLinks.linkBoxType = "preview";
      if (window.BGLinks.searchPage) {
        window.BGLinks.searchPage();
      }
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    const url = `https://www.biblegateway.com/quicksearch/?search=${encodeURIComponent(searchQuery)}&version=${version}`;
    window.open(url, "_blank");
  };

  const handleQuickVerse = (ref) => {
    const url = `https://www.biblegateway.com/passage/?search=${encodeURIComponent(ref)}&version=${version}`;
    window.open(url, "_blank");
  };

  return (
    <section id="bible" className="section-spacing bg-white" data-testid="bible-search-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: Info */}
          <div>
            <p className="text-overline mb-4" data-testid="bible-overline">Bible Gateway</p>
            <div className="accent-bar mb-8" />
            <h2 className="heading-massive text-4xl sm:text-5xl text-black mb-8" data-testid="bible-heading">
              Search the<br />
              Word.
            </h2>
            <p className="font-['Satoshi'] text-lg text-zinc-600 leading-relaxed mb-10" data-testid="bible-description">
              Follow along during service or dive deeper into Scripture anytime. Search any verse, chapter, or keyword across multiple Bible translations.
            </p>

            {/* Quick Verses */}
            <div>
              <p className="font-['Clash_Display'] font-semibold text-lg mb-4">Popular Verses</p>
              <div className="flex flex-wrap gap-3" data-testid="popular-verses">
                {popularVerses.map((verse) => (
                  <button
                    key={verse.ref}
                    onClick={() => handleQuickVerse(verse.ref)}
                    className="px-4 py-2 border border-zinc-200 font-['Satoshi'] text-sm text-zinc-700 hover:bg-black hover:text-white hover:border-black transition-all duration-300"
                    data-testid={`verse-btn-${verse.ref.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    {verse.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Search Form */}
          <div className="card-hard-shadow bg-zinc-50 p-8 md:p-12" data-testid="bible-search-form-container">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-black flex items-center justify-center">
                <BookOpen size={24} className="text-white" />
              </div>
              <h3 className="font-['Clash_Display'] font-semibold text-2xl">
                Search the Bible
              </h3>
            </div>

            <form onSubmit={handleSearch} className="space-y-5" data-testid="bible-search-form">
              <div>
                <label className="font-['Satoshi'] font-medium text-sm mb-2 block">
                  Enter verse or keyword
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="e.g. John 3:16 or faith"
                    className="input-sharp h-12 pr-10"
                    data-testid="bible-search-input"
                  />
                  <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                </div>
              </div>

              <div>
                <label className="font-['Satoshi'] font-medium text-sm mb-2 block">
                  Bible Version
                </label>
                <Select onValueChange={setVersion} value={version}>
                  <SelectTrigger className="input-sharp h-12" data-testid="bible-version-select">
                    <SelectValue placeholder="Select version" />
                  </SelectTrigger>
                  <SelectContent>
                    {bibleVersions.map((v) => (
                      <SelectItem key={v.value} value={v.value}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                className="btn-sharp bg-black text-white hover:bg-white hover:text-black hover:shadow-[inset_0_0_0_2px_black] px-8 py-6 text-sm w-full flex items-center justify-center gap-2"
                data-testid="bible-search-submit"
              >
                <Search size={18} />
                Search Bible Gateway
              </Button>
            </form>

            <p className="font-['Satoshi'] text-center text-zinc-400 text-xs mt-6 flex items-center justify-center gap-1">
              Powered by
              <a
                href="https://www.biblegateway.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-500 hover:text-black transition-colors inline-flex items-center gap-1"
                data-testid="bible-gateway-link"
              >
                BibleGateway.com <ExternalLink size={12} />
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BibleSearch;
